import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["M", "T", "W", "T", "F", "S", "S"];

function pad2(n) { return String(n).padStart(2, "0"); }
export function isoDate(d) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }

/** Compact month-block: 7-column grid with M–S headings and clickable date cells. */
export default function MiniMonth({ year, month, selectedIso, onSelect, onShiftMonth, showNav = "both" }) {
  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    // Convert Sun=0..Sat=6 to Mon=0..Sun=6 (weeks start on Monday to match the screenshot).
    const startDow = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrev = new Date(year, month, 0).getDate();
    const cells = [];
    // Leading grey days from previous month
    for (let i = startDow; i > 0; i--) {
      cells.push({ y: month === 0 ? year - 1 : year, m: month === 0 ? 11 : month - 1, d: daysInPrev - i + 1, muted: true });
    }
    for (let d = 1; d <= daysInMonth; d++) cells.push({ y: year, m: month, d, muted: false });
    // Trail into next month to complete 6 rows (42 cells)
    while (cells.length % 7 !== 0 || cells.length < 42) {
      const last = cells[cells.length - 1];
      const nd = new Date(last.y, last.m, last.d + 1);
      cells.push({ y: nd.getFullYear(), m: nd.getMonth(), d: nd.getDate(), muted: nd.getMonth() !== month });
      if (cells.length >= 42) break;
    }
    return cells;
  }, [year, month]);

  return (
    <div className="text-[11px] leading-none w-full max-w-[150px] mx-auto" data-testid={`mini-month-${year}-${month + 1}`}>
      <div className="flex items-center justify-between mb-0.5">
        {(showNav === "both" || showNav === "left") ? (
          <button
            type="button"
            onClick={() => onShiftMonth && onShiftMonth(-1)}
            className="p-0.5 rounded hover:bg-secondary"
            aria-label="Previous month"
            data-testid="mini-month-prev"
          ><ChevronLeft className="w-3 h-3" /></button>
        ) : <span className="w-5" />}
        <span className="font-medium">{MONTH_NAMES[month]} {year}</span>
        {(showNav === "both" || showNav === "right") ? (
          <button
            type="button"
            onClick={() => onShiftMonth && onShiftMonth(1)}
            className="p-0.5 rounded hover:bg-secondary"
            aria-label="Next month"
            data-testid="mini-month-next"
          ><ChevronRight className="w-3 h-3" /></button>
        ) : <span className="w-5" />}
      </div>
      <div className="grid grid-cols-7 gap-0 text-center text-muted-foreground mb-0 leading-none">
        {DOW.map((c, i) => <span key={i} className="py-0 px-0 h-3 flex items-center justify-center">{c}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-0 text-center">
        {cells.map((c, i) => {
          const cellIso = `${c.y}-${pad2(c.m + 1)}-${pad2(c.d)}`;
          const active = selectedIso === cellIso;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onSelect && onSelect(new Date(c.y, c.m, c.d))}
              className={
                "h-5 min-w-0 px-0 rounded hover:bg-secondary/70 transition-colors leading-none flex items-center justify-center " +
                (c.muted ? "text-muted-foreground/50 " : "") +
                (active ? "bg-primary text-primary-foreground hover:bg-primary/90" : "")
              }
              data-testid={`mini-day-${cellIso}`}
            >
              {c.d}
            </button>
          );
        })}
      </div>
    </div>
  );
}
